import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaArrowUp, FaEye } from "react-icons/fa";
import { Link, Outlet } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";
import Header from "../shared/Header";

const TaiorMain = ({ SIDEBAR_WIDTH, isDashboardRoute, onSidebarOpen }) => {
  const [stats, setStats] = useState([]);
  const [orders, setOrders] = useState([]);
  const [type, setType] = useState("assigned");
  const toast = useToast();
  const [loadingStatusId, setLoadingStatusId] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchStats();
    fetchOrders();
    // eslint-disable-next-line
  }, [type]);

  const fetchUser = async () => {
    try {
      const res = await axiosInstance.get("/api/users/me");
      setUser(res.data);
    } catch (err) {
      console.error("Failed to fetch user", err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get("/api/master-stats/assigned-tailor-stats");
      const s = res.data.stats;
      setStats([
        { label: "Assigned", value: s.totalAssigned, color: "blue.400" },
        { label: "Completed", value: s.totalCompleted, color: "green.400" },
        { label: "In Progress", value: s.totalInProgress, color: "orange.400" },
        { label: "Cancelled", value: s.totalCancelled, color: "red.400" },
      ]);
    } catch (err) {
      console.error("Error fetching stats", err);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axiosInstance.get(`/api/master-stats/tailor-order`, {
        params: {
          status: type,
          page: 1,
          limit: 10,
        },
      });

      const groupedOrders = {};

      res.data.orders.forEach((o) => {
        if (!o.orderDetail) return;

        const key = o.billNumber;

        if (!groupedOrders[key]) {
          groupedOrders[key] = {
            orderId: o._id,
            billNumber: o.billNumber,
            created: new Date(o.orderDate).toLocaleDateString("en-IN"),
            assignedBy: o.createdBy || "Receptionist",
            items: [],
          };
        }

        groupedOrders[key].items.push({
          orderDetailId: o.orderDetail._id,
          item: o.orderDetail.item || "-",
          due: o.orderDetail.deliveryDate
            ? new Date(o.orderDetail.deliveryDate).toLocaleDateString("en-IN")
            : "-",
          status: o.orderDetail?.measurements?.status || "in-progress",
        });
      });

      setOrders(Object.values(groupedOrders));
    } catch (err) {
      console.error("Error fetching orders", err);
    }
  };

  const handleStatusChange = async (orderId, orderDetailId, newStatus) => {
    try {
      setLoadingStatusId(orderId + "-" + orderDetailId);
      await axiosInstance.put(
        `/api/orders/${orderId}/status`,
        {
          orderDetailId,
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast({
        title: "Status updated successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchStats();
      fetchOrders();
    } catch (err) {
      toast({
        title: "Failed to update status",
        description: err.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoadingStatusId(null);
    }
  };

  const currentDate = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <Box ml={{ base: 0, md: SIDEBAR_WIDTH }}>
      <Header onSidebarOpen={onSidebarOpen} user={user} />

      <Box p={4} bg="gray.50" minH="calc(100vh - 64px)">
        <Text fontSize="sm" color="gray.600" mb={4}>
          Today: {currentDate}
        </Text>

        {isDashboardRoute ? (
          <>
            <SimpleGrid columns={[1, 2, 4]} spacing={4} mb={8}>
              {stats.map((s, i) => (
                <Box
                  key={i}
                  bg="white"
                  p={4}
                  rounded="md"
                  shadow="sm"
                  borderLeft="4px solid"
                  borderColor={s.color}
                >
                  <Text fontSize="sm" color="gray.600">
                    {s.label}
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {s.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            <Text fontWeight="semibold">Select Type</Text>
            <Text fontSize="sm" color="gray.500" mb={4}>
              Select what you want to view now
            </Text>

            <SimpleGrid columns={[1, 2]} spacing={4} mb={10}>
              <Box
                p={6}
                rounded="md"
                border="2px solid"
                borderColor={type === "assigned" ? "blue.400" : "gray.300"}
                onClick={() => setType("assigned")}
                cursor="pointer"
              >
                <Text fontSize="lg" fontWeight="semibold">
                  Assigned Orders
                </Text>
              </Box>
              <Box
                p={6}
                rounded="md"
                border="2px solid"
                borderColor={type === "completed" ? "green.400" : "gray.300"}
                onClick={() => setType("completed")}
                cursor="pointer"
              >
                <Text fontSize="lg" fontWeight="semibold">
                  Completed Orders
                </Text>
              </Box>
            </SimpleGrid>

            <Box bg="white" p={4} rounded="md" boxShadow="sm">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Tailor Orders</Heading>
                <Button size="sm" variant="ghost" rightIcon={<FaArrowUp />}>
                  Show All
                </Button>
              </Flex>

              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>S.No</Th>
                      <Th>Bill No</Th>
                      <Th>Items</Th>
                      <Th>Date Created</Th>
                      <Th>Due Dates</Th>
                      <Th>Measurements</Th>
                      <Th>Assigned By</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {orders.map((order, index) => (
                      <Tr key={order.billNumber}>
                        <Td>{index + 1}</Td>
                        <Td>{order.billNumber}</Td>
                        <Td>
                          {order.items.map((i, idx) => (
                            <Text key={idx}>{i.item}</Text>
                          ))}
                        </Td>
                        <Td>{order.created}</Td>
                        <Td fontWeight="bold">
                          {order.items.map((i, idx) => (
                            <Text key={idx}>{i.due}</Text>
                          ))}
                        </Td>
                        <Td>
                          <Link to={`/dashboard/order/${order.orderId}`}>
                            <IconButton
                              icon={<FaEye />}
                              size="sm"
                              aria-label="View"
                            />
                          </Link>
                        </Td>
                        <Td>{order.assignedBy}</Td>
                        <Td>
                          {order.items.map((i, idx) => (
                            <Box key={idx} mb={1}>
                              <Select
                                size="sm"
                                value={i.status}
                                onChange={(e) =>
                                  handleStatusChange(
                                    order.orderId,
                                    i.orderDetailId,
                                    e.target.value
                                  )
                                }
                                isDisabled={
                                  loadingStatusId ===
                                  order.orderId + "-" + i.orderDetailId
                                }
                              >
                                <option value="in-progress">In Progress</option>
                                <option value="done">Done</option>
                                <option value="cancel">Cancel</option>
                              </Select>
                              {loadingStatusId ===
                                order.orderId + "-" + i.orderDetailId && (
                                <Spinner size="xs" ml={2} />
                              )}
                            </Box>
                          ))}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </>
        ) : (
          <Outlet />
        )}
      </Box>
    </Box>
  );
};

export default TaiorMain;
